import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, Plus, Key, Lock, LockOpen, Trash } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POSTES = [
  'Chef de chantier',
  'Conducteur de travaux',
  'Ouvrier',
  'Commercial',
  'Direction',
  'Autre',
];

// PLA-10 — message selon le vrai statut d'envoi ; RP06 — sans e-mail, le lien
// d'invitation est affiché pour être transmis (sinon le compte est inutilisable).
const notifierAcces = (data, okMsg) => {
  if (data?.email_sent) return toast.success(okMsg);
  if (data?.activation_url) {
    toast.warning(data.message || 'E-mail non envoyé');
    window.prompt("E-mail non envoyé. Lien d'invitation à transmettre :", data.activation_url);
    return undefined;
  }
  return toast.info(data?.message || 'Email non envoyé');
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export default function MonEquipe({ user }) {
  // A6 (P2#8) — compte créé par une invitation à un appel d'offres : rattaché à l'annuaire
  // par `linked_entreprise_id` (accepté par /entreprises/{id}/employes)
  const entrepriseId = user?.parent_entreprise_id || user?.entreprise_id || user?.linked_entreprise_id;
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = async () => {
    if (!entrepriseId) return setLoading(false);
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/entreprises/${entrepriseId}/employes`, { withCredentials: true });
      setEmployes(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [entrepriseId]);

  const handleResetPassword = async (id) => {
    if (!window.confirm("Renvoyer l'invitation à ce salarié ? Il recevra un lien pour choisir son mot de passe.")) return;
    try {
      // PLA-08 — ouvert au patron pour ses employés ; PLA-10 — statut d'envoi réel
      const { data } = await axios.post(`${API_URL}/api/admin/clients/${id}/reset-password`, {}, { withCredentials: true });
      notifierAcces(data, 'Invitation renvoyée par e-mail');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleStatus = async (emp) => {
    try {
      // PLA-08 — endpoint de blocage commun (le patron y est autorisé pour ses employés)
      const { data } = await axios.post(`${API_URL}/api/admin/users/${emp.id}/toggle-block`, {}, { withCredentials: true });
      toast.success(data?.is_active === false ? 'Compte bloqué' : 'Compte débloqué');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  if (!entrepriseId) {
    // A6 (P2#8) — plus d'encadré technique : sans rattachement, le bloc ne s'affiche pas
    return null;
  }

  return (
    <div data-testid="mon-equipe">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={18} weight="bold" className="text-[#C4773B]" />
          <h2 className="text-base font-medium text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>Mon équipe</h2>
          <span className="text-xs text-[#6B6B6B]">({employes.length})</span>
        </div>
        <Button onClick={() => setShowAdd(true)} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none gap-2" data-testid="add-employe-btn">
          <Plus size={16} weight="bold" /> Inviter un salarié
        </Button>
      </div>

      {loading ? (
        <p className="text-xs text-[#6B6B6B]">Chargement...</p>
      ) : employes.length === 0 ? (
        <div className="bg-white border border-[#E0E0DB] p-6 text-center text-sm text-[#6B6B6B] italic">
          Aucun salarié rattaché. Invitez-les pour leur donner accès à l'application.
        </div>
      ) : (
        <div className="space-y-2">
          {employes.map((emp) => (
            <div key={emp.id} className="bg-white border border-[#E0E0DB] p-4 flex items-start justify-between" data-testid={`employe-row-${emp.id}`}>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-[#2C2C2C]">{emp.name}</span>
                  {emp.poste && <span className="text-xs text-[#6B6B6B]">— {emp.poste}</span>}
                  {!emp.is_active && <span className="text-[10px] bg-[#FFEBEE] text-[#8B3A3A] px-1.5 py-0.5 border border-[#8B3A3A]">Bloqué</span>}
                </div>
                <p className="text-xs text-[#6B6B6B]">{emp.email}</p>
                {emp.telephone && <p className="text-xs text-[#6B6B6B]">{emp.telephone}</p>}
                <p className="text-[10px] text-[#9A9A9A] mt-1">
                  {emp.last_login_at ? `Dernière connexion : ${fmtDate(emp.last_login_at)}` : 'Jamais connecté'}
                  {emp.login_count > 0 && ` · ${emp.login_count} connexion${emp.login_count > 1 ? 's' : ''}`}
                </p>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-none h-7 w-7 p-0" onClick={() => handleResetPassword(emp.id)} title="Renvoyer l'invitation">
                  <Key size={12} />
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-7 w-7 p-0" onClick={() => handleToggleStatus(emp)} title={emp.is_active ? 'Bloquer' : 'Débloquer'}>
                  {emp.is_active ? <Lock size={12} /> : <LockOpen size={12} />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddEmployeDialog
          entrepriseId={entrepriseId}
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function AddEmployeDialog({ entrepriseId, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', email: '', telephone: '', poste: 'Chef de chantier' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Nom et email requis');
    try {
      setSaving(true);
      const { data } = await axios.post(`${API_URL}/api/entreprises/${entrepriseId}/employes`, form, { withCredentials: true });
      notifierAcces(data, 'Salarié invité. E-mail d\'invitation envoyé.');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="add-employe-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Inviter un salarié</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Nom complet *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" required data-testid="input-employe-name" />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none mt-1" required data-testid="input-employe-email" />
          </div>
          <div>
            <Label className="text-xs">Téléphone</Label>
            <Input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} className="rounded-none mt-1" placeholder="+33612345678" />
          </div>
          <div>
            <Label className="text-xs">Poste</Label>
            <Select value={form.poste} onValueChange={(v) => setForm({ ...form, poste: v })}>
              <SelectTrigger className="rounded-none mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {POSTES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="bg-[#FFF5EB] border border-[#F0CDA6] p-2 text-xs text-[#6B4A25]">
            Le salarié recevra un e-mail avec un lien pour choisir son mot de passe.
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#C4773B] hover:bg-[#A85F25] rounded-none" data-testid="submit-add-employe">
              {saving ? 'Ajout...' : 'Ajouter'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
